import useForkRef from '@mui/material/utils/useForkRef';
import _ from 'lodash';
import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { ControllerRenderProps, RefCallBack } from 'react-hook-form';
import {
	get,
	useController,
	useFormContext,
	useFormState,
} from 'react-hook-form';

import { useErrorMessage } from './use-error-message';

// Add string map to allow for values to be interpolated in error message
type UseFieldOptions = {
	name: string;
	label?: string;
	defaultValue?: unknown;
	disableRefWarning?: boolean;
} & Record<string, unknown>;

interface TypedFieldProps<T> extends ControllerRenderProps {
	value: T;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useField<T = any>({
	name,
	label,
	defaultValue,
	disableRefWarning,
	...context
}: UseFieldOptions) {
	const formMethods = useFormContext();

	if (!formMethods) {
		throw new Error(
			'Could not get form context. Did you forget to use FormProvider?'
		);
	}

	const controllerMethods = useController({
		name,
		defaultValue,
		control: formMethods.control,
	});

	// Subscribe to form errors to ensure re-renders when errors change
	const { errors } = useFormState({
		control: formMethods.control,
	});

	/**
	 * Extract the final field name from a nested path.
	 *
	 * `foo.bar.baz` -> `baz`
	 *
	 * `foo` -> `foo`
	 *
	 * `foo.bar.0.baz.1` -> `baz` (removes array indices)
	 */
	const errorLabel =
		name.split('.').findLast((part) => !/^\d+$/u.test(part)) || name;
	/**
	 * Errors do not seem stable in react-hook-form.
	 * This may need changed to something more robust.
	 */
	const errorMessage = useErrorMessage(get(errors, name), {
		label:
			errorLabel === 'avbId' ? 'AVB ID' : (label ?? _.startCase(errorLabel)),
		...context,
	});

	const hookRef = useRef<HTMLElement | null>(null);
	const ref = useForkRef(controllerMethods.field.ref, hookRef);

	useErrorFocus(errorMessage, hookRef);

	return {
		...controllerMethods,
		field: {
			...controllerMethods.field,
			ref: useUnplacedRefWarning(ref, name, disableRefWarning),
		} as TypedFieldProps<T>,
		errorMessage,
		formMethods,
	};
}

/**
 * Log a warning if the field's ref is not placed.
 *
 * Field refs should (almost) always be placed so that react-hook-form
 * can scroll the field in to view when it has an error.
 */
// TODO Should this be disabled/noop in production?
function useUnplacedRefWarning(
	originalFieldRef: RefCallBack | null,
	name: string,
	disableRefWarning?: boolean
) {
	const fieldRefPlaced = useRef(false);

	const fieldRef = useCallback<RefCallBack>(
		(instance) => {
			originalFieldRef?.(instance);
			fieldRefPlaced.current = !!instance;
		},
		[originalFieldRef]
	);

	useEffect(
		() => {
			if (!fieldRefPlaced.current && !disableRefWarning) {
				console.warn('Ref was not placed for field %o.', name);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[fieldRefPlaced.current]
	);

	return fieldRef;
}

function useErrorFocus(
	error: string | undefined,
	ref: MutableRefObject<HTMLElement | null>
) {
	useEffect(() => {
		/**
		 * Focus the field if it has an error. Normally, this
		 * focus should be set automically by
		 * `formMethods.setError(field, err, { shouldFocus: true })`
		 * but it doesn't work for some reason (even when using
		 * a registered input as included in the docs).
		 */
		if (error) {
			ref.current?.focus();
		}
	}, [error, ref]);
}
